import { useStickyState } from '@/hooks/useStickyState';
import { isLovablePreview } from '@/lib/utils/environment';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import {
  useGetProductsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
} from '@/lib/graphql/types-and-hooks';
import type { Product } from '@/components/settings/ProductsSettings';

// Minimal structural shape fromBackend reads — satisfied by the getProducts
// query result (a narrower inline type than the full generated Product type).
type ProductLike = {
  id: string;
  properties?: { name?: string | null; description?: string | null; productCode?: string | null; unitPrice?: number | null } | null;
};

const SEED_PRODUCTS: Product[] = [
  { id: 'p1', name: 'Coffee Single Shot', description: 'Solo seat plan, billed monthly.', sku: 'COF-SS-01', listPrice: 15 },
  { id: 'p2', name: 'Coffee Tall', description: 'Up to 5 seats, billed monthly.', sku: 'COF-TL-05', listPrice: 25 },
  { id: 'p3', name: 'Coffee Grande', description: 'Up to 25 seats, billed monthly.', sku: 'COF-GR-25', listPrice: 50 },
  { id: 'p4', name: 'Implementation Services', description: 'One-time onboarding & training package.', sku: 'SVC-IMPL-01', listPrice: 2500 },
];

function fromBackend(gql: ProductLike): Product {
  return {
    id: gql.id,
    name: gql.properties?.name ?? '',
    description: gql.properties?.description ?? '',
    sku: gql.properties?.productCode ?? '',
    listPrice: gql.properties?.unitPrice ?? null,
  };
}

/**
 * Workspace product catalog hook with dual-mode support.
 *
 * Preview mode: sticky state (shared with ProductsSettings component).
 * Production mode: backend GraphQL CRUD.
 */
export function useProducts() {
  const isPreview = isLovablePreview();
  const workspaceId = useWorkspaceId();

  const [previewProducts, setPreviewProducts] = useStickyState<Product[]>('workspace-products', SEED_PRODUCTS);

  const { data, loading, refetch } = useGetProductsQuery({
    variables: { workspaceId: workspaceId ?? '' },
    skip: !workspaceId || isPreview,
  });

  const [createProductMutation] = useCreateProductMutation();
  const [updateProductMutation] = useUpdateProductMutation();
  const [deleteProductMutation] = useDeleteProductMutation();

  const products: Product[] = isPreview
    ? previewProducts
    : (data?.products ?? []).filter((p): p is NonNullable<typeof p> => p != null).map(fromBackend);

  const createProduct = async (input: Omit<Product, 'id'>) => {
    if (isPreview) {
      setPreviewProducts(prev => [...prev, { ...input, id: `p${Date.now()}` }]);
      return;
    }
    if (!workspaceId) return;
    await createProductMutation({
      variables: {
        input: {
          workspaceId,
          name: input.name,
          ...(input.sku && { productCode: input.sku }),
          ...(input.description && { description: input.description }),
          ...(input.listPrice != null && { unitPrice: input.listPrice }),
        },
      },
    });
    await refetch();
  };

  const updateProduct = async (id: string, input: Partial<Omit<Product, 'id'>>) => {
    if (isPreview) {
      setPreviewProducts(prev => prev.map(p => p.id === id ? { ...p, ...input } : p));
      return;
    }
    if (!workspaceId) return;
    await updateProductMutation({
      variables: {
        input: {
          id,
          workspaceId,
          ...(input.name !== undefined && { name: input.name }),
          ...(input.sku !== undefined && { productCode: input.sku }),
          ...(input.description !== undefined && { description: input.description }),
          ...(input.listPrice !== undefined && { unitPrice: input.listPrice ?? undefined }),
        },
      },
    });
    await refetch();
  };

  const deleteProduct = async (id: string) => {
    if (isPreview) {
      setPreviewProducts(prev => prev.filter(p => p.id !== id));
      return;
    }
    if (!workspaceId) return;
    await deleteProductMutation({
      variables: { id, workspaceId },
    });
    await refetch();
  };

  return { products, loading, createProduct, updateProduct, deleteProduct };
}
